/*
 * What each removal says, kept apart from the dialog that says it so the wording can be checked
 * without a browser. Every destructive action names what it removes, from this closed list, and the
 * wording lives here rather than at the call site — so removing an item, a feed, a
 * Connector account, a consent to reading Connector accounts, a connected agent, an access token, an Identity, a group, a Member from a group, a Member's Admin
 * role, a Member's access, a duplicate Member merged away, a grant on a Project, a ticket, an Epic,
 * a comment, a Chat message, an assignee, a relation, an attachment, a channel rule, a Status, an
 * Area, a Label, the monthly budget, a Linked repo, unsaved text, someone in a Chat channel, the
 * Member's own place in a private one or in a group direct message all read alike, and a page
 * cannot ask a generic "Are you sure?".
 */

export type Removal =
  | { removes: "item"; name: string }
  | { removes: "feed"; name: string }
  /**
   * The Member's own account at a Connector, by the Connector's name, with `giveBack`, the
   * sentence for what becomes of its access at the service (`describeDisconnecting`).
   */
  | { removes: "connector-account"; connector: string; giveBack: string }
  /**
   * A change request driver's consent to its agent reading their Connector accounts, by the
   * Connectors' names ("Sentry and Airtable") and the change request as its page names it.
   */
  | { removes: "connector-consent"; connectors: string; changeRequest: string }
  /** One of the Member's connected agents, by its name and domain: "Claude Code · claude.ai". */
  | { removes: "connected-agent"; agent: string }
  /** One of the Member's access tokens, by the name they gave it. */
  | { removes: "access-token"; name: string }
  /**
   * An Identity, as a Profile page shows it: an email, a mobile number. `primary` when it is their
   * primary email, which another verified email then becomes. `of` names the Member it is taken
   * from when an Admin takes it from someone else; without it, it is the Member's own.
   */
  | { removes: "identity"; value: string; primary?: boolean; of?: string }
  | { removes: "group"; name: string }
  | { removes: "group-membership"; member: string; group: string }
  /** `self` when the Member is removing their own role, and so their own way back to the Admin pages. */
  | { removes: "admin-role"; member: string; self?: boolean }
  /** A Member's access to Beacon, by deactivating them; their history keeps their name. */
  | { removes: "member-access"; member: string }
  /** A duplicate Member, merged into the one kept, who takes everything theirs. */
  | { removes: "duplicate-member"; merged: string; kept: string }
  | { removes: "project-grant"; grantee: string; project: string }
  | { removes: "ticket"; ticket: string }
  | { removes: "epic"; epic: string }
  | { removes: "comment" }
  /**
   * A Chat message, for everyone who can read it: one of the Member's own, or, with `author`,
   * someone else's that a manager of its channel removes.
   */
  /**
   * A Chat message: someone else's when `author` is named, carrying `files` when it does, with how
   * many `replies` its thread holds, which stay.
   */
  | { removes: "chat-message"; author?: string; files?: number; replies?: number }
  | { removes: "assignee"; member: string; ticket: string }
  | { removes: "relation"; from: string; to: string }
  | { removes: "attachment"; name: string }
  | { removes: "channel-rule"; channel: string }
  /** A Status, with how many tickets it holds and the Status they move to, when it holds some. */
  | { removes: "status"; name: string; tickets: number; moveTo: string | null }
  /**
   * An Area, with how many tickets and Epics are in it and the Area they move to; none leaves them
   * with no Area.
   */
  | { removes: "area"; name: string; tickets: number; epics: number; moveTo: string | null }
  /** A Label, with how many tickets and Epics carry it. */
  | { removes: "label"; name: string; tickets: number; epics: number }
  | { removes: "budget" }
  /** A repository a Project links, by its full name, from the Project by its key. */
  | { removes: "linked-repo"; name: string; project: string }
  /** A Linked PR, by `repo#number`, unlinked from the ticket `ticket` keys. */
  | { removes: "linked-pr"; pull: string; ticket: string }
  /** Words being written, lost when what holds them closes, as `UnsavedText` says what they were. */
  | ({ removes: "unsaved-text" } & UnsavedText)
  /** A change request its driver abandons, as its page names it: "OPS change request 3". */
  | { removes: "change-request"; changeRequest: string }
  /** Someone a channel's manager removes from it, `private` when only its participants read it. */
  | { removes: "chat-participant"; member: string; channel: string; private: boolean }
  /** The Member's own place in a private channel, which their reading of it goes with. */
  | { removes: "private-channel-membership"; channel: string }
  /**
   * The Member's own place in a group direct message, `with` naming its others as its header does
   * ("Ada and Ben"): their reading of it goes with it, for good.
   */
  | { removes: "group-dm-membership"; with: string }
  /** One of the company's custom emoji, which an Admin removes for everyone, by its `:name:`. */
  | { removes: "custom-emoji"; name: string }
  /**
   * A channel's bookmark, which its manager takes out for everyone who reads `channel`, named by
   * how it shows (`label`): what it links to stays where it is.
   */
  | { removes: "chat-bookmark"; label: string; channel: string }

/**
 * What unsaved words were, which the question asks after. By default, or `changes`, changes to a
 * text that exists ("Discard changes?"), which `what` names, a ticket's description or comment
 * unless the page says. `draft`, words never sent, as a change request's chat and Answers mostly
 * are ("Discard your draft?"), `what` naming where they were written. Or something never created,
 * which names itself: a new ticket, Epic or Project ("Discard this ticket?").
 */
export type UnsavedText =
  { discards?: "changes" | "draft"; what?: string } | { discards: NeverCreated }

/** Something a Composer was making, lost before it was ever created. */
export type NeverCreated = "new-ticket" | "new-epic" | "new-project" | "new-channel"

/** What each thing never created is called in its question: "Discard this Epic?". */
const neverCreated = {
  "new-ticket": "ticket",
  "new-epic": "Epic",
  "new-project": "Project",
  "new-channel": "channel",
} as const satisfies Record<NeverCreated, string>

/** A Discard question's title and where it says the words were written. */
function unsavedOf(unsaved: UnsavedText): { title: string; where: string } {
  switch (unsaved.discards) {
    case undefined:
    case "changes":
      return {
        title: "Discard changes?",
        // A named place is where it was written already; only the default needs "here".
        where: unsaved.what ?? "the description or a comment here",
      }
    case "draft":
      return { title: "Discard your draft?", where: unsaved.what ?? "your draft" }
    default: {
      const made = neverCreated[unsaved.discards]
      return { title: `Discard this ${made}?`, where: `the new ${made}` }
    }
  }
}

/**
 * A confirmation's words. `trigger` labels a button that opens it, `menu` a menu item (ending in an
 * ellipsis, since it asks before it acts), and `label` is the accessible name of an icon that does,
 * so it still says what goes. `subject`, when there is one, is the name inside `title` that may be
 * too long to show whole, such as an email, so the dialog can shorten it and offer it in full.
 */
export type Wording = {
  trigger: string
  menu: string
  label: string
  title: string
  subject?: string
  consequence: string
  confirm: string
}

/** Tickets and Epics counted as a consequence names them: "3 tickets and 2 Epics", "ticket". */
const countedOf = ({ tickets, epics }: { tickets: number; epics: number }) =>
  [
    ...(tickets ? [tickets === 1 ? "ticket" : `${tickets} tickets`] : []),
    ...(epics ? [epics === 1 ? "Epic" : `${epics} Epics`] : []),
  ].join(" and ")

export function wordingOf(removal: Removal): Wording {
  switch (removal.removes) {
    case "item":
      return {
        trigger: "Remove",
        menu: "Remove item…",
        label: `Remove “${removal.name}”`,
        title: `Remove “${removal.name}” from the knowledge base?`,
        subject: removal.name,
        consequence: "Beacon stops answering from it. It stays where it came from.",
        confirm: "Remove item",
      }
    case "feed":
      return {
        trigger: "Remove",
        menu: "Remove feed…",
        label: `Remove the feed “${removal.name}”`,
        title: `Remove the feed “${removal.name}”?`,
        subject: removal.name,
        consequence: "Its items leave the knowledge base, and Beacon stops syncing it.",
        confirm: "Remove feed",
      }
    case "connector-account":
      return {
        trigger: "Disconnect",
        menu: "Disconnect…",
        label: `Disconnect your ${removal.connector} account`,
        title: `Disconnect your ${removal.connector} account?`,
        consequence: `Beacon's agent stops using it at once. ${removal.giveBack} What Beacon already posted, filed or changed with it stays, and an action already under way there isn't undone.`,
        confirm: "Disconnect",
      }
    case "connector-consent":
      return {
        trigger: "Withdraw consent",
        menu: "Withdraw consent…",
        label: `Withdraw your consent to reading ${removal.connectors}`,
        title: `Withdraw your consent to reading ${removal.connectors}?`,
        consequence: `The agent reads nothing more from them in ${removal.changeRequest}, from its next step on. What it already wrote stays: its findings, Questions, Spec, proposals, Ticket plan and any tickets created from it.`,
        confirm: "Withdraw consent",
      }
    case "connected-agent":
      return {
        trigger: "Revoke",
        menu: "Revoke…",
        label: `Revoke ${removal.agent}`,
        title: `Revoke ${removal.agent}?`,
        subject: removal.agent,
        consequence:
          "Its next call to Beacon is refused. To use Beacon from it again, connect it again.",
        confirm: "Revoke",
      }
    case "access-token":
      return {
        trigger: "Revoke",
        menu: "Revoke…",
        label: `Revoke the access token “${removal.name}”`,
        title: `Revoke the access token “${removal.name}”?`,
        subject: removal.name,
        consequence:
          "The next call made with it is refused. A script that uses it needs a new token.",
        confirm: "Revoke",
      }
    case "identity":
      return {
        trigger: "Remove",
        menu: "Remove…",
        label: `Remove ${removal.value}`,
        title: removal.of
          ? `Remove ${removal.value} from ${removal.of}?`
          : `Remove ${removal.value} from your account?`,
        subject: removal.value,
        consequence: removal.of
          ? removal.primary
            ? "Beacon stops recognising them by it, and writes to another of their verified emails instead."
            : "Beacon stops recognising them by it. You can give it back later."
          : removal.primary
            ? "Beacon stops recognising you by it, and writes to another of your verified emails instead."
            : "Beacon stops recognising you by it. You can add it again later.",
        confirm: "Remove",
      }
    case "member-access":
      return {
        trigger: "Deactivate",
        menu: "Deactivate…",
        label: `Deactivate ${removal.member}`,
        title: `Deactivate ${removal.member}?`,
        subject: removal.member,
        consequence:
          "They're signed out, and can't sign in, ask Beacon or use their connected agents and access tokens. Everything they did keeps their name. An Admin can reactivate them.",
        confirm: "Deactivate",
      }
    case "duplicate-member":
      return {
        trigger: "Merge",
        menu: "Merge…",
        label: `Merge ${removal.merged} into ${removal.kept}`,
        title: `Merge ${removal.merged} into ${removal.kept}?`,
        consequence: `Everything ${removal.merged} has in Beacon becomes ${removal.kept}'s: their emails and accounts, Groups, access, tickets and history. Then ${removal.merged} is gone. A merge can't be undone.`,
        confirm: "Merge",
      }
    case "group":
      return {
        trigger: "Delete",
        menu: "Delete group…",
        label: `Delete the group “${removal.name}”`,
        title: `Delete the group “${removal.name}”?`,
        subject: removal.name,
        consequence:
          "Its Members stay Members, but anything shared with the group stops reaching them through it.",
        confirm: "Delete group",
      }
    case "group-membership":
      return {
        trigger: "Remove",
        menu: "Remove from group…",
        label: `Remove ${removal.member} from ${removal.group}`,
        title: `Remove ${removal.member} from ${removal.group}?`,
        subject: removal.member,
        consequence: `Anything shared with ${removal.group} stops reaching them through it.`,
        confirm: "Remove from group",
      }
    case "admin-role":
      return removal.self
        ? {
            trigger: "Remove Admin",
            menu: "Remove Admin…",
            label: "Remove your own Admin role",
            title: "Remove your own Admin role?",
            consequence: "You stay a Member, but lose access to Members, Groups, Agent and Spend.",
            confirm: "Remove Admin",
          }
        : {
            trigger: "Remove Admin",
            menu: "Remove Admin…",
            label: `Remove ${removal.member}'s Admin role`,
            title: `Remove ${removal.member}'s Admin role?`,
            subject: removal.member,
            consequence:
              "They stay a Member, but can no longer manage Members, groups or the knowledge base.",
            confirm: "Remove Admin",
          }
    case "project-grant":
      return {
        trigger: "Revoke",
        menu: "Revoke access…",
        label: `Revoke ${removal.grantee}'s access to ${removal.project}`,
        title: `Revoke ${removal.grantee}'s access to ${removal.project}?`,
        subject: removal.grantee,
        consequence:
          "Whatever other grants they hold on it still apply; this one stops giving them anything.",
        confirm: "Revoke access",
      }
    case "ticket":
      return {
        trigger: "Delete",
        menu: "Delete ticket…",
        label: `Delete ${removal.ticket}`,
        title: `Delete ${removal.ticket}?`,
        consequence:
          "It goes, with its history. The Project's Activity keeps who deleted it, and its number is never used again.",
        confirm: "Delete ticket",
      }
    case "epic":
      return {
        trigger: "Delete",
        menu: "Delete Epic…",
        label: `Delete ${removal.epic}`,
        title: `Delete ${removal.epic}?`,
        consequence:
          "It goes, with its history. Its tickets stay where they are, in no Epic. The Project's Activity keeps who deleted it, and its number is never used again.",
        confirm: "Delete Epic",
      }
    case "comment":
      return {
        trigger: "Delete",
        menu: "Delete…",
        label: "Delete your comment",
        title: "Delete your comment?",
        consequence: "It goes for everyone who can read it, and can't be brought back.",
        confirm: "Delete comment",
      }
    case "chat-message": {
      const consequence = messageConsequence(removal.files ?? 0, removal.replies ?? 0)
      if (removal.author !== undefined)
        return {
          trigger: "Remove",
          menu: "Remove message…",
          label: `Remove ${removal.author}'s message`,
          title: `Remove ${removal.author}'s message?`,
          subject: removal.author,
          consequence,
          confirm: "Remove message",
        }
      return {
        trigger: "Delete",
        menu: "Delete message…",
        label: "Delete your message",
        title: "Delete your message?",
        consequence,
        confirm: "Delete message",
      }
    }
    case "assignee":
      return {
        trigger: "Remove",
        menu: "Take off…",
        label: `Take ${removal.member} off ${removal.ticket}`,
        title: `Take ${removal.member} off ${removal.ticket}?`,
        subject: removal.member,
        consequence:
          "Only a Member with Write can be assigned again, so someone who has lost it can't be put back.",
        confirm: "Take them off",
      }
    case "relation":
      return {
        trigger: "Remove",
        menu: "Remove link…",
        label: `Remove the relation between ${removal.from} and ${removal.to}`,
        title: `Remove the relation between ${removal.from} and ${removal.to}?`,
        consequence:
          "Both tickets stop showing it, and each one's Activity records who removed it.",
        confirm: "Remove relation",
      }
    case "attachment":
      return {
        trigger: "Remove",
        menu: "Remove file…",
        label: `Remove ${removal.name}`,
        title: `Remove ${removal.name}?`,
        subject: removal.name,
        consequence:
          "The file is deleted for everyone who can read it, and anywhere it is shown in the text shows its name instead.",
        confirm: "Remove file",
      }
    case "channel-rule":
      return {
        trigger: "Remove",
        menu: "Remove rule…",
        label: `Stop posting to ${removal.channel}`,
        title: `Stop posting to ${removal.channel}?`,
        subject: removal.channel,
        consequence:
          "Beacon posts nothing more from this Project there. Nothing already posted goes.",
        confirm: "Remove rule",
      }
    case "status":
      return {
        trigger: "Remove",
        menu: "Remove Status…",
        label: `Remove the Status “${removal.name}”`,
        title: `Remove the Status “${removal.name}”?`,
        subject: removal.name,
        consequence:
          removal.tickets === 0
            ? "Its column leaves the Board, and channel rules stop posting moves into it."
            : `Its ${removal.tickets === 1 ? "ticket moves" : `${removal.tickets} tickets move`} to ${removal.moveTo ?? "the Status you choose"}, and channel rules stop posting moves into it.`,
        confirm: "Remove Status",
      }
    case "area": {
      const counted = countedOf(removal)
      const one = removal.tickets + removal.epics === 1
      const goes = removal.moveTo
        ? `${one ? "moves" : "move"} to ${removal.moveTo}`
        : `${one ? "is" : "are"} left with no Area`
      return {
        trigger: "Delete",
        menu: "Delete Area…",
        label: `Delete the Area “${removal.name}”`,
        title: `Delete the Area “${removal.name}”?`,
        subject: removal.name,
        consequence: counted
          ? `Its ${counted} ${goes}, and ${one ? "its" : "each one's"} Activity records it.`
          : "Nothing is in it, so no ticket or Epic changes.",
        confirm: "Delete Area",
      }
    }
    case "label": {
      const counted = countedOf(removal)
      const one = removal.tickets + removal.epics === 1
      return {
        trigger: "Delete",
        menu: "Delete Label…",
        label: `Delete the Label “${removal.name}”`,
        title: `Delete the Label “${removal.name}”?`,
        subject: removal.name,
        consequence: counted
          ? `It comes off the ${counted} that ${one ? "carries" : "carry"} it.`
          : "Nothing carries it, so no ticket or Epic changes.",
        confirm: "Delete Label",
      }
    }
    case "budget":
      return {
        trigger: "Remove budget",
        menu: "Remove budget…",
        label: "Remove the monthly budget",
        title: "Remove the monthly budget?",
        consequence: "Admins stop getting Slack DMs at 80% and 100%.",
        confirm: "Remove budget",
      }
    case "linked-repo":
      return {
        trigger: "Unlink",
        menu: "Unlink repository…",
        label: `Unlink ${removal.name} from ${removal.project}`,
        title: `Unlink ${removal.name} from ${removal.project}?`,
        subject: removal.name,
        consequence: `${removal.project} stops picking up its pull requests. Nothing on GitHub changes.`,
        confirm: "Unlink repository",
      }
    case "linked-pr":
      return {
        trigger: "Unlink",
        menu: "Unlink pull request…",
        label: `Unlink ${removal.pull} from ${removal.ticket}`,
        title: `Unlink ${removal.pull} from ${removal.ticket}?`,
        subject: removal.pull,
        consequence: `${removal.ticket} stops showing it, even when it changes on GitHub, until someone links it again by its URL. Nothing on GitHub changes.`,
        confirm: "Unlink pull request",
      }
    case "unsaved-text": {
      const { title, where } = unsavedOf(removal)
      return {
        trigger: "Discard",
        menu: "Discard…",
        label: "Discard changes",
        title,
        consequence: `What you've written in ${where} hasn't been saved.`,
        confirm: "Discard",
      }
    }
    case "change-request":
      return {
        trigger: "Abandon…",
        menu: "Abandon…",
        label: `Abandon ${removal.changeRequest}`,
        title: `Abandon ${removal.changeRequest}?`,
        consequence:
          "The agent stops, and nothing more is answered or created from it. It stays on the " +
          "record, out of the Change requests list unless someone shows abandoned ones.",
        confirm: "Abandon",
      }
    case "chat-participant":
      return {
        trigger: "Remove",
        menu: "Remove from channel…",
        label: `Remove ${removal.member} from #${removal.channel}`,
        title: `Remove ${removal.member} from #${removal.channel}?`,
        subject: removal.member,
        consequence: removal.private
          ? "They stop reading it at once, its earlier messages too, until someone adds them back."
          : "They stop taking part, though they can still read it and join again.",
        confirm: "Remove",
      }
    case "private-channel-membership":
      return {
        trigger: "Leave channel",
        menu: "Leave channel…",
        label: `Leave #${removal.channel}`,
        title: `Leave #${removal.channel}?`,
        subject: `#${removal.channel}`,
        consequence:
          "You stop reading it at once, its earlier messages too. Only someone who manages it can add you back.",
        confirm: "Leave channel",
      }
    case "group-dm-membership":
      return {
        trigger: "Leave conversation",
        menu: "Leave conversation…",
        label: `Leave the conversation with ${removal.with}`,
        title: `Leave the conversation with ${removal.with}?`,
        subject: removal.with,
        consequence: `You stop reading it at once, its earlier messages too, and nobody can add you back. ${removal.with} keep it.`,
        confirm: "Leave conversation",
      }
    case "custom-emoji":
      return {
        trigger: "Remove",
        menu: "Remove emoji…",
        label: `Remove :${removal.name}:`,
        title: `Remove :${removal.name}:?`,
        subject: `:${removal.name}:`,
        consequence:
          "Nobody can use it any more, and its name is free for another. Messages that used it show its name instead, and reactions with it stay until people take them back.",
        confirm: "Remove emoji",
      }
    case "chat-bookmark":
      return {
        trigger: "Remove",
        menu: "Remove bookmark…",
        label: `Remove the bookmark ${removal.label}`,
        title: `Remove ${removal.label} from the bookmarks of #${removal.channel}?`,
        subject: removal.label,
        consequence: `Nobody in #${removal.channel} finds it among its bookmarks any more. What it links to stays where it is.`,
        confirm: "Remove bookmark",
      }
  }
}

/**
 * A title cut around its subject, so the subject can be shortened on screen and shown in full on
 * demand while the words either side stay whole. A title without its subject is all `before`.
 */
export function titleParts({ title, subject }: Pick<Wording, "title" | "subject">) {
  const at = subject ? title.indexOf(subject) : -1
  if (!subject || at < 0) return { before: title, subject: undefined, after: "" }
  return { before: title.slice(0, at), subject, after: title.slice(at + subject.length) }
}

/**
 * What deleting a Chat message does, and what it can't undo: a copy anyone already has stays, and
 * for its `files`, a download already made or a link already handed out, which works until it
 * lapses. Its thread, when it has `replies`, stays with them, under a note that it was deleted.
 */
function messageConsequence(files: number, replies = 0) {
  const carried = files === 0 ? "" : `, ${files === 1 ? "its file" : `its ${files} files`} too`
  const thread =
    replies === 0
      ? ""
      : ` Its thread stays, with ${replies === 1 ? "its reply" : `its ${replies} replies`}, under a note that the message was deleted.`
  const downloads =
    files === 0
      ? "."
      : `, and anyone who downloaded ${files === 1 ? "the file" : "the files"} keeps their copy; a download link already handed out works for up to 5 minutes more.`
  return `It goes for everyone in the conversation${carried}, and can't be brought back.${thread} Anyone who already read, copied or was notified of it may still have it${downloads}`
}
